import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Button,
  Input,
  Select,
  FormControl,
  FormErrorMessage,
  FormLabel,
  SimpleGrid,
  HStack,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { loadApplyFormData, saveApplyFormData } from "./applyFormStorage";
import { saveCustomerProfileStep } from "./applyProfileApi";

const incomeTypeOptions = [
  "Employment",
  "Self-Employment",
  "Employmen Insurance (EI)",
  "Canada Child Benefit (CCB)",
  "Private Pension",
  "Social Security",
  "Disability",
  "Retirement",
  "Canada Pension/Old Age Security",
  "Other",
];

const payrollTypeOptions = ["Direct deposit", "Cheque", "Cash"];
const paymentFrequencyOptions = ["Weekly", "Bi-weekly", "Semi-monthly", "Monthly"];
const weekdayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const industryOptions = [
  "Accounting",
  "Agriculture",
  "Automotive",
  "Banking",
  "Construction",
  "Consulting",
  "Customer Service",
  "Education",
  "Energy",
  "Engineering",
  "Entertainment",
  "Finance",
  "Food Services",
  "Government",
  "Healthcare",
  "Hospitality",
  "Human Resources",
  "Information Technology",
  "Insurance",
  "Legal",
  "Logistics",
  "Manufacturing",
  "Marketing",
  "Media",
  "Mining",
  "Non-Profit",
  "Pharmaceutical",
  "Public Safety",
  "Real Estate",
  "Retail",
  "Sales",
  "Telecommunications",
  "Transportation",
  "Utilities",
  "Warehousing",
  "Other",
];
const monthOptions = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const currentYear = new Date().getFullYear();
const employedSinceYearOptions = Array.from({ length: 60 }, (_, index) => String(currentYear - index));
const employmentStatusOptions = ["Full-time (30+ hours/week)", "Part-time", "Contract", "Temporary", "Seasonal", "Probation", "Other"];

const getDayWithSuffix = (day) => {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }

  const lastDigit = day % 10;

  if (lastDigit === 1) {
    return `${day}st`;
  }

  if (lastDigit === 2) {
    return `${day}nd`;
  }

  if (lastDigit === 3) {
    return `${day}rd`;
  }

  return `${day}th`;
};

const dayOfMonthOptions = Array.from({ length: 31 }, (_, index) => getDayWithSuffix(index + 1));
const dayOfMonthWithLastOptions = [...dayOfMonthOptions, "Last"];
const semiMonthlyDayOptions = [...dayOfMonthWithLastOptions, "Other"];

const createAdditionalIncomeSection = () => ({
  id: `${Date.now()}-${Math.random()}`,
  incomeType: "",
  netMonthlyIncome: "",
  payrollType: "",
  paymentFrequency: "",
  payday: "",
  semiMonthlyOption: "",
  semiMonthlyFirstDay: "",
  semiMonthlySecondDay: "",
  semiMonthlyOtherPaydays: "",
  semiMonthlyOrdinal: "",
  semiMonthlyWeekday: "",
  semiMonthlySecondOrdinal: "",
  semiMonthlySecondWeekday: "",
});

const getAdditionalPaydayValidity = (section) => {
  const sectionIsWeeklyOrBiweekly = section.paymentFrequency === "Weekly" || section.paymentFrequency === "Bi-weekly";
  const sectionIsMonthly = section.paymentFrequency === "Monthly";
  const sectionIsSemiMonthly = section.paymentFrequency === "Semi-monthly";

  if ((sectionIsMonthly || sectionIsWeeklyOrBiweekly) && section.payday) {
    return true;
  }

  if (sectionIsSemiMonthly && section.semiMonthlyFirstDay && section.semiMonthlySecondDay) {
    const hasOtherPayday = section.semiMonthlyFirstDay === "Other" || section.semiMonthlySecondDay === "Other";
    return hasOtherPayday ? Boolean(section.semiMonthlyOtherPaydays) : true;
  }

  return false;
};

const assignIfEntered = (target, key, value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed) {
      target[key] = trimmed;
    }
    return;
  }

  if (value !== null && value !== undefined && value !== "") {
    target[key] = value;
  }
};

const buildAdditionalIncomePayload = (section) => {
  const payload = {};
  assignIfEntered(payload, "incomeType", section.incomeType);
  assignIfEntered(payload, "netMonthlyIncome", section.netMonthlyIncome);
  assignIfEntered(payload, "payrollType", section.payrollType);
  assignIfEntered(payload, "paymentFrequency", section.paymentFrequency);

  if (section.paymentFrequency === "Weekly" || section.paymentFrequency === "Bi-weekly" || section.paymentFrequency === "Monthly") {
    assignIfEntered(payload, "payday", section.payday);
  }

  if (section.paymentFrequency === "Semi-monthly") {
    assignIfEntered(payload, "semiMonthlyOption", section.semiMonthlyOption);
    assignIfEntered(payload, "semiMonthlyFirstDay", section.semiMonthlyFirstDay);
    assignIfEntered(payload, "semiMonthlySecondDay", section.semiMonthlySecondDay);
    assignIfEntered(payload, "semiMonthlyOtherPaydays", section.semiMonthlyOtherPaydays);
    assignIfEntered(payload, "semiMonthlyOrdinal", section.semiMonthlyOrdinal);
    assignIfEntered(payload, "semiMonthlyWeekday", section.semiMonthlyWeekday);
    assignIfEntered(payload, "semiMonthlySecondOrdinal", section.semiMonthlySecondOrdinal);
    assignIfEntered(payload, "semiMonthlySecondWeekday", section.semiMonthlySecondWeekday);
  }

  return payload;
};

const buildIncomePayload = (values, additionalIncomeSections) => {
  const payload = {};
  assignIfEntered(payload, "incomeType", values.incomeType);
  assignIfEntered(payload, "netMonthlyIncome", values.netMonthlyIncome);
  assignIfEntered(payload, "payrollType", values.payrollType);
  assignIfEntered(payload, "paymentFrequency", values.paymentFrequency);
  assignIfEntered(payload, "lastPayday", values.lastPayday);
  assignIfEntered(payload, "nextPayday", values.nextPayday);

  if (values.incomeType === "Employment") {
    assignIfEntered(payload, "employerCompanyName", values.employerCompanyName);
    assignIfEntered(payload, "employerPhoneNumber", values.employerPhoneNumber);
    assignIfEntered(payload, "position", values.position);
    assignIfEntered(payload, "industry", values.industry);
    assignIfEntered(payload, "employedSinceYear", values.employedSinceYear);
    assignIfEntered(payload, "employedSinceMonth", values.employedSinceMonth);
    assignIfEntered(payload, "employmentStatus", values.employmentStatus);
  }

  if (values.paymentFrequency === "Weekly" || values.paymentFrequency === "Bi-weekly" || values.paymentFrequency === "Monthly") {
    assignIfEntered(payload, "payday", values.payday);
  }

  if (values.paymentFrequency === "Semi-monthly") {
    assignIfEntered(payload, "semiMonthlyOption", values.semiMonthlyOption);
    assignIfEntered(payload, "semiMonthlyFirstDay", values.semiMonthlyFirstDay);
    assignIfEntered(payload, "semiMonthlySecondDay", values.semiMonthlySecondDay);
    assignIfEntered(payload, "semiMonthlyOtherPaydays", values.semiMonthlyOtherPaydays);
    assignIfEntered(payload, "semiMonthlyOrdinal", values.semiMonthlyOrdinal);
    assignIfEntered(payload, "semiMonthlyWeekday", values.semiMonthlyWeekday);
    assignIfEntered(payload, "semiMonthlySecondOrdinal", values.semiMonthlySecondOrdinal);
    assignIfEntered(payload, "semiMonthlySecondWeekday", values.semiMonthlySecondWeekday);
  }

  const cleanedAdditionalIncomeSections = additionalIncomeSections
    .map((section) => buildAdditionalIncomePayload(section))
    .filter((section) => Object.keys(section).length > 0);

  if (cleanedAdditionalIncomeSections.length > 0) {
    payload.additionalIncomeSections = cleanedAdditionalIncomeSections;
  }

  return payload;
};

export default function ApplyIncomeInfoPage() {
  const navigate = useNavigate();

  const initialData = useMemo(() => {
    const stored = loadApplyFormData();
    const income = stored.income || {};

    const normalizePaydayByFrequency = (frequency, payday, allowEmpty = false) => {
      if (frequency === "Monthly") {
        if (dayOfMonthWithLastOptions.includes(payday)) {
          return payday;
        }

        return allowEmpty ? "" : dayOfMonthWithLastOptions[0];
      }

      if (frequency === "Weekly" || frequency === "Bi-weekly") {
        if (weekdayOptions.includes(payday)) {
          return payday;
        }

        return allowEmpty ? "" : weekdayOptions[0];
      }

      return "";
    };

    const initialPaymentFrequency = "";

    const initialPayday = "";

    const initialAdditionalIncomeSections = Array.isArray(income.additionalIncomeSections)
      ? income.additionalIncomeSections.map((section) => {
          const sectionPaymentFrequency = section.paymentFrequency || "";

          const sectionPayday = normalizePaydayByFrequency(sectionPaymentFrequency, section.payday, true);
          const shouldKeepSectionSemiMonthlyFields = sectionPaymentFrequency === "Semi-monthly";

          return {
            ...createAdditionalIncomeSection(),
            ...section,
            paymentFrequency: sectionPaymentFrequency,
            payday: sectionPayday,
            semiMonthlyFirstDay: shouldKeepSectionSemiMonthlyFields ? section.semiMonthlyFirstDay || "" : "",
            semiMonthlySecondDay: shouldKeepSectionSemiMonthlyFields ? section.semiMonthlySecondDay || "" : "",
            semiMonthlyOtherPaydays: shouldKeepSectionSemiMonthlyFields ? section.semiMonthlyOtherPaydays || "" : "",
          };
        })
      : [];

    const shouldKeepMainSemiMonthlyFields = initialPaymentFrequency === "Semi-monthly";

    return {
      values: {
        incomeType: income.incomeType || "",
        employerCompanyName: income.employerCompanyName || "",
        employerPhoneNumber: income.employerPhoneNumber || "",
        position: income.position || "",
        industry: income.industry || "",
        employedSinceYear: income.employedSinceYear || "",
        employedSinceMonth: income.employedSinceMonth || "",
        employmentStatus: income.employmentStatus || "",
        netMonthlyIncome: income.netMonthlyIncome || "",
        payrollType: income.payrollType || "",
        paymentFrequency: initialPaymentFrequency,
        payday: initialPayday,
        lastPayday: income.lastPayday || "",
        nextPayday: income.nextPayday || "",
        semiMonthlyOption: income.semiMonthlyOption || "",
        semiMonthlyFirstDay: shouldKeepMainSemiMonthlyFields ? income.semiMonthlyFirstDay || "" : "",
        semiMonthlySecondDay: shouldKeepMainSemiMonthlyFields ? income.semiMonthlySecondDay || "" : "",
        semiMonthlyOtherPaydays: shouldKeepMainSemiMonthlyFields ? income.semiMonthlyOtherPaydays || "" : "",
        semiMonthlyOrdinal: income.semiMonthlyOrdinal || "",
        semiMonthlyWeekday: income.semiMonthlyWeekday || "",
        semiMonthlySecondOrdinal: income.semiMonthlySecondOrdinal || "",
        semiMonthlySecondWeekday: income.semiMonthlySecondWeekday || "",
      },
      additionalIncomeSections: initialAdditionalIncomeSections,
    };
  }, []);

  const [values, setValues] = useState(initialData.values);
  const [additionalIncomeSections, setAdditionalIncomeSections] = useState(initialData.additionalIncomeSections);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [touched, setTouched] = useState({
    incomeType: false,
    employerCompanyName: false,
    employerPhoneNumber: false,
    position: false,
    industry: false,
    employedSinceYear: false,
    employedSinceMonth: false,
    employmentStatus: false,
    netMonthlyIncome: false,
    payrollType: false,
    paymentFrequency: false,
    payday: false,
    lastPayday: false,
    nextPayday: false,
    additionalIncome: false,
  });
  const [focusedField, setFocusedField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const isWeeklyOrBiweekly = values.paymentFrequency === "Weekly" || values.paymentFrequency === "Bi-weekly";
  const isMonthly = values.paymentFrequency === "Monthly";
  const isSemiMonthly = values.paymentFrequency === "Semi-monthly";
  const hasPaymentFrequency = Boolean(values.paymentFrequency);
  const isEmploymentIncome = values.incomeType === "Employment";
  const isSemiMonthlyOtherSelected = values.semiMonthlyFirstDay === "Other" || values.semiMonthlySecondDay === "Other";
  const filteredIndustryOptions = industryOptions.filter((option) => option.toLowerCase().includes(values.industry.toLowerCase()));

  const isPaydayValid =
    (isMonthly && Boolean(values.payday)) ||
    (isWeeklyOrBiweekly && Boolean(values.payday)) ||
    (isSemiMonthly &&
      Boolean(values.semiMonthlyFirstDay) &&
      Boolean(values.semiMonthlySecondDay) &&
      (!isSemiMonthlyOtherSelected || Boolean(values.semiMonthlyOtherPaydays)));

  const hasIncompleteAdditionalIncome = additionalIncomeSections.some(
    (section) =>
      !section.incomeType || !section.netMonthlyIncome || !section.payrollType || !section.paymentFrequency || !getAdditionalPaydayValidity(section)
  );

  const paydayError =
    touched.payday && !values.paymentFrequency
      ? "Payment frequency is required"
      : touched.payday && isMonthly && !values.payday
      ? "Please select a payday"
      : touched.payday && isWeeklyOrBiweekly && !values.payday
      ? "Please select your payday"
      : touched.payday && isSemiMonthly && (!values.semiMonthlyFirstDay || !values.semiMonthlySecondDay)
      ? "Please select both semi-monthly days"
      : touched.payday && isSemiMonthly && isSemiMonthlyOtherSelected && !values.semiMonthlyOtherPaydays
      ? "Please enter paydays"
      : "";

  const additionalIncomeError =
    touched.additionalIncome && hasIncompleteAdditionalIncome
      ? "Please complete all added income fields or remove the section"
      : "";

  const errors = {
    incomeType: touched.incomeType && !values.incomeType ? "Income type is required" : "",
    employerCompanyName: isEmploymentIncome && touched.employerCompanyName && !values.employerCompanyName ? "Employer company name is required" : "",
    employerPhoneNumber: isEmploymentIncome && touched.employerPhoneNumber && !values.employerPhoneNumber ? "Employer phone number is required" : "",
    position: isEmploymentIncome && touched.position && !values.position ? "Position is required" : "",
    industry: isEmploymentIncome && touched.industry && !values.industry ? "Industry is required" : "",
    employedSinceYear: isEmploymentIncome && touched.employedSinceYear && !values.employedSinceYear ? "Employed since year is required" : "",
    employedSinceMonth: isEmploymentIncome && touched.employedSinceMonth && !values.employedSinceMonth ? "Employed since month is required" : "",
    employmentStatus: isEmploymentIncome && touched.employmentStatus && !values.employmentStatus ? "Employment status is required" : "",
    netMonthlyIncome: touched.netMonthlyIncome && !values.netMonthlyIncome ? "Net monthly income is required" : "",
    payrollType: touched.payrollType && !values.payrollType ? "Payroll type is required" : "",
    paymentFrequency: touched.paymentFrequency && !values.paymentFrequency ? "Payment frequency is required" : "",
    payday: paydayError,
    lastPayday: touched.lastPayday && !values.lastPayday ? "Last payday is required" : "",
    nextPayday: touched.nextPayday && !values.nextPayday ? "Next payday is required" : "",
    additionalIncome: additionalIncomeError,
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const persistIncome = () => {
    saveApplyFormData({
      income: buildIncomePayload(values, additionalIncomeSections),
    });
  };

  const setAllTouched = () => {
    setTouched({
      incomeType: true,
      employerCompanyName: true,
      employerPhoneNumber: true,
      position: true,
      industry: true,
      employedSinceYear: true,
      employedSinceMonth: true,
      employmentStatus: true,
      netMonthlyIncome: true,
      payrollType: true,
      paymentFrequency: true,
      payday: true,
      lastPayday: true,
      nextPayday: true,
      additionalIncome: true,
    });
  };

  const handleChange = (field) => (event) => {
    const nextValue = event.target.value;

    if (field === "incomeType") {
      setValues((prev) => ({
        ...prev,
        incomeType: nextValue,
        employerCompanyName: nextValue === "Employment" ? prev.employerCompanyName : "",
        employerPhoneNumber: nextValue === "Employment" ? prev.employerPhoneNumber : "",
        position: nextValue === "Employment" ? prev.position : "",
        industry: nextValue === "Employment" ? prev.industry : "",
        employedSinceYear: nextValue === "Employment" ? prev.employedSinceYear : "",
        employedSinceMonth: nextValue === "Employment" ? prev.employedSinceMonth : "",
        employmentStatus: nextValue === "Employment" ? prev.employmentStatus : "",
      }));

      if (nextValue !== "Employment") {
        setTouched((prev) => ({
          ...prev,
          employerCompanyName: false,
          employerPhoneNumber: false,
          position: false,
          industry: false,
          employedSinceYear: false,
          employedSinceMonth: false,
          employmentStatus: false,
        }));
      }

      return;
    }

    if (field === "paymentFrequency") {
      setValues((prev) => ({
        ...prev,
        paymentFrequency: nextValue,
        payday: "",
        semiMonthlyOption: "",
        semiMonthlyFirstDay: "",
        semiMonthlySecondDay: "",
        semiMonthlyOtherPaydays: "",
        semiMonthlyOrdinal: "",
        semiMonthlyWeekday: "",
        semiMonthlySecondOrdinal: "",
        semiMonthlySecondWeekday: "",
      }));
      setTouched((prev) => ({ ...prev, payday: false }));
      return;
    }

    if (field === "semiMonthlyFirstDay" || field === "semiMonthlySecondDay") {
      setValues((prev) => {
        const nextValues = { ...prev, [field]: nextValue };
        const hasOtherPayday =
          (field === "semiMonthlyFirstDay" ? nextValue : nextValues.semiMonthlyFirstDay) === "Other" ||
          (field === "semiMonthlySecondDay" ? nextValue : nextValues.semiMonthlySecondDay) === "Other";

        if (!hasOtherPayday) {
          nextValues.semiMonthlyOtherPaydays = "";
        }

        return nextValues;
      });
      return;
    }

    if (field === "semiMonthlyOption") {
      setValues((prev) => ({
        ...prev,
        semiMonthlyOption: nextValue,
        semiMonthlyFirstDay: nextValue === "dayPair" ? prev.semiMonthlyFirstDay : "",
        semiMonthlySecondDay: nextValue === "dayPair" ? prev.semiMonthlySecondDay : "",
        semiMonthlyOrdinal: nextValue === "ordinalWeekday" ? prev.semiMonthlyOrdinal : "",
        semiMonthlyWeekday: nextValue === "ordinalWeekday" ? prev.semiMonthlyWeekday : "",
        semiMonthlySecondOrdinal: nextValue === "ordinalWeekday" ? prev.semiMonthlySecondOrdinal : "",
        semiMonthlySecondWeekday: nextValue === "ordinalWeekday" ? prev.semiMonthlySecondWeekday : "",
        payday: "",
      }));
      return;
    }

    setValues((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleAdditionalIncomeChange = (id, field) => (input) => {
    const nextValue = typeof input === "string" ? input : input.target.value;

    setAdditionalIncomeSections((prev) =>
      prev.map((section) => {
        if (section.id !== id) {
          return section;
        }

        if (field === "paymentFrequency") {
          return {
            ...section,
            paymentFrequency: nextValue,
            payday: "",
            semiMonthlyOption: "",
            semiMonthlyFirstDay: "",
            semiMonthlySecondDay: "",
            semiMonthlyOtherPaydays: "",
            semiMonthlyOrdinal: "",
            semiMonthlyWeekday: "",
            semiMonthlySecondOrdinal: "",
            semiMonthlySecondWeekday: "",
          };
        }

        if (field === "semiMonthlyFirstDay" || field === "semiMonthlySecondDay") {
          const nextSection = { ...section, [field]: nextValue };
          const hasOtherPayday =
            (field === "semiMonthlyFirstDay" ? nextValue : nextSection.semiMonthlyFirstDay) === "Other" ||
            (field === "semiMonthlySecondDay" ? nextValue : nextSection.semiMonthlySecondDay) === "Other";

          if (!hasOtherPayday) {
            nextSection.semiMonthlyOtherPaydays = "";
          }

          return nextSection;
        }

        if (field === "semiMonthlyOption") {
          return {
            ...section,
            semiMonthlyOption: nextValue,
            semiMonthlyFirstDay: nextValue === "dayPair" ? section.semiMonthlyFirstDay : "",
            semiMonthlySecondDay: nextValue === "dayPair" ? section.semiMonthlySecondDay : "",
            semiMonthlyOrdinal: nextValue === "ordinalWeekday" ? section.semiMonthlyOrdinal : "",
            semiMonthlyWeekday: nextValue === "ordinalWeekday" ? section.semiMonthlyWeekday : "",
            semiMonthlySecondOrdinal: nextValue === "ordinalWeekday" ? section.semiMonthlySecondOrdinal : "",
            semiMonthlySecondWeekday: nextValue === "ordinalWeekday" ? section.semiMonthlySecondWeekday : "",
            payday: "",
          };
        }

        return { ...section, [field]: nextValue };
      })
    );
  };

  const handleAddIncomeSection = () => {
    setAdditionalIncomeSections((prev) => [...prev, createAdditionalIncomeSection()]);
    setTouched((prev) => ({ ...prev, additionalIncome: true }));
  };

  const handleRemoveIncomeSection = (id) => {
    setAdditionalIncomeSections((prev) => prev.filter((section) => section.id !== id));
  };

  const handleFocus = (field) => () => {
    setFocusedField(field);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField((prev) => (prev === field ? "" : prev));
  };

  const isFloating = (field) => focusedField === field || values[field] !== "";

  const handleBack = () => {
    persistIncome();
    const stored = loadApplyFormData();
    const flow = stored.flow && typeof stored.flow === "object" ? stored.flow : {};
    const incomeBackPath = flow.incomeBackPath === "/apply/reapply" ? "/apply/reapply" : "/apply/personal-info";
    navigate(incomeBackPath);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    setAllTouched();
    if (
      !values.incomeType ||
      (isEmploymentIncome &&
        (!values.employerCompanyName ||
          !values.employerPhoneNumber ||
          !values.position ||
          !values.industry ||
          !values.employedSinceYear ||
          !values.employedSinceMonth ||
          !values.employmentStatus)) ||
      !values.netMonthlyIncome ||
      !values.payrollType ||
      !values.paymentFrequency ||
      !isPaydayValid ||
      !values.lastPayday ||
      !values.nextPayday ||
      hasIncompleteAdditionalIncome ||
      hasErrors
    ) {
      return;
    }

    try {
      setIsSubmitting(true);
      const incomePayload = buildIncomePayload(values, additionalIncomeSections);
      saveApplyFormData({ income: incomePayload });
      await saveCustomerProfileStep("income", incomePayload);
      navigate("/apply/bank-details");
    } catch (error) {
      setSubmitError(error.message || "Unable to save your profile right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">Income Sources</Heading>
            </VStack>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired isInvalid={Boolean(errors.incomeType)} position="relative" gridColumn={{ base: "span 1", md: "span 2" }}>
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("incomeType") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("incomeType") ? "xs" : "sm"}
                      color={isFloating("incomeType") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Income type
                    </FormLabel>
                    <Select
                      value={values.incomeType}
                      onChange={handleChange("incomeType")}
                      onFocus={handleFocus("incomeType")}
                      onBlur={handleBlur("incomeType")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    >
                      <option value="" disabled hidden />
                      {incomeTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.incomeType}</FormErrorMessage>
                  </FormControl>

                  {isEmploymentIncome && (
                    <>
                      <Box gridColumn={{ base: "span 1", md: "span 2" }}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          <FormControl isRequired isInvalid={Boolean(errors.employerCompanyName)} position="relative">
                            <FormLabel
                              position="absolute"
                              left="12px"
                              top={isFloating("employerCompanyName") ? "-9px" : "13px"}
                              zIndex={2}
                              px={2}
                              bg="white"
                              fontSize={isFloating("employerCompanyName") ? "xs" : "sm"}
                              color={isFloating("employerCompanyName") ? "gray.700" : "gray.500"}
                              pointerEvents="none"
                              transition="all 0.2s ease"
                              m={0}
                            >
                              Employer Company Name
                            </FormLabel>
                            <Input
                              value={values.employerCompanyName}
                              onChange={handleChange("employerCompanyName")}
                              onFocus={handleFocus("employerCompanyName")}
                              onBlur={handleBlur("employerCompanyName")}
                              bg="white"
                              h="52px"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              placeholder=""
                              required
                            />
                            <FormErrorMessage>{errors.employerCompanyName}</FormErrorMessage>
                          </FormControl>

                          <FormControl isRequired isInvalid={Boolean(errors.employerPhoneNumber)} position="relative">
                            <FormLabel
                              position="absolute"
                              left="12px"
                              top={isFloating("employerPhoneNumber") ? "-9px" : "13px"}
                              zIndex={2}
                              px={2}
                              bg="white"
                              fontSize={isFloating("employerPhoneNumber") ? "xs" : "sm"}
                              color={isFloating("employerPhoneNumber") ? "gray.700" : "gray.500"}
                              pointerEvents="none"
                              transition="all 0.2s ease"
                              m={0}
                            >
                              Employer Phone Number
                            </FormLabel>
                            <Input
                              type="tel"
                              value={values.employerPhoneNumber}
                              onChange={handleChange("employerPhoneNumber")}
                              onFocus={handleFocus("employerPhoneNumber")}
                              onBlur={handleBlur("employerPhoneNumber")}
                              bg="white"
                              h="52px"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              placeholder=""
                              required
                            />
                            <FormErrorMessage>{errors.employerPhoneNumber}</FormErrorMessage>
                          </FormControl>
                        </SimpleGrid>
                      </Box>

                      <Box gridColumn={{ base: "span 1", md: "span 2" }} borderTop="1px solid" borderColor="gray.200" my={1} />

                      <FormControl isRequired isInvalid={Boolean(errors.position)} position="relative">
                        <FormLabel
                          position="absolute"
                          left="12px"
                          top={isFloating("position") ? "-9px" : "13px"}
                          zIndex={2}
                          px={2}
                          bg="white"
                          fontSize={isFloating("position") ? "xs" : "sm"}
                          color={isFloating("position") ? "gray.700" : "gray.500"}
                          pointerEvents="none"
                          transition="all 0.2s ease"
                          m={0}
                        >
                          Position
                        </FormLabel>
                        <Input
                          value={values.position}
                          onChange={handleChange("position")}
                          onFocus={handleFocus("position")}
                          onBlur={handleBlur("position")}
                          bg="white"
                          h="52px"
                          borderRadius="lg"
                          borderColor="gray.300"
                          _focus={{ borderColor: "black", boxShadow: "none" }}
                          _hover={{ borderColor: "gray.400" }}
                          placeholder=""
                          required
                        />
                        <FormErrorMessage>{errors.position}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={Boolean(errors.industry)} position="relative">
                        <FormLabel
                          position="absolute"
                          left="12px"
                          top={isFloating("industry") ? "-9px" : "13px"}
                          zIndex={2}
                          px={2}
                          bg="white"
                          fontSize={isFloating("industry") ? "xs" : "sm"}
                          color={isFloating("industry") ? "gray.700" : "gray.500"}
                          pointerEvents="none"
                          transition="all 0.2s ease"
                          m={0}
                        >
                          Industry
                        </FormLabel>
                        <Input
                          value={values.industry}
                          onChange={handleChange("industry")}
                          onFocus={() => {
                            handleFocus("industry")();
                            setIsIndustryDropdownOpen(true);
                          }}
                          onBlur={() => {
                            handleBlur("industry")();
                            setTimeout(() => setIsIndustryDropdownOpen(false), 120);
                          }}
                          bg="white"
                          h="52px"
                          borderRadius="lg"
                          borderColor="gray.300"
                          _focus={{ borderColor: "black", boxShadow: "none" }}
                          _hover={{ borderColor: "gray.400" }}
                          placeholder=""
                          required
                        />
                        {isIndustryDropdownOpen && (
                          <Box
                            position="absolute"
                            top="calc(100% + 6px)"
                            left={0}
                            width="100%"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            boxShadow="sm"
                            zIndex={5}
                            maxH="180px"
                            overflowY="scroll"
                            sx={{
                              scrollbarWidth: "thick",
                              scrollbarColor: "#A0AEC0 transparent",
                              "&::-webkit-scrollbar": {
                                width: "20px",
                              },
                              "&::-webkit-scrollbar-track": {
                                background: "transparent",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                background: "#A0AEC0",
                                borderRadius: "10px",
                                border: "1px solid transparent",
                                backgroundClip: "content-box",
                              },
                              "&::-webkit-scrollbar-thumb:hover": {
                                background: "#718096",
                                backgroundClip: "content-box",
                              },
                            }}
                          >
                            {filteredIndustryOptions.length > 0 ? (
                              filteredIndustryOptions.map((option) => (
                                <Box
                                  key={option}
                                  px={3}
                                  py={2}
                                  fontSize="sm"
                                  cursor="pointer"
                                  _hover={{ bg: "gray.50" }}
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    setValues((prev) => ({ ...prev, industry: option }));
                                    setIsIndustryDropdownOpen(false);
                                  }}
                                >
                                  {option}
                                </Box>
                              ))
                            ) : (
                              <Box px={3} py={2} fontSize="sm" color="gray.500">
                                No matching industries
                              </Box>
                            )}
                          </Box>
                        )}
                        <FormErrorMessage>{errors.industry}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={Boolean(errors.employedSinceYear || errors.employedSinceMonth)}>
                        <FormLabel m={0} mb={2} color="gray.700" fontSize="sm">
                          Employed Since
                        </FormLabel>
                        <HStack spacing={2} align="center">
                          <Select
                            value={values.employedSinceYear}
                            onChange={handleChange("employedSinceYear")}
                            onBlur={handleBlur("employedSinceYear")}
                            bg="white"
                            h="40px"
                            fontSize="sm"
                            borderRadius="lg"
                            borderColor="gray.300"
                            _focus={{ borderColor: "black", boxShadow: "none" }}
                            _hover={{ borderColor: "gray.400" }}
                            maxW="120px"
                            required
                          >
                            <option value="" disabled>
                              yyyy
                            </option>
                            {employedSinceYearOptions.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </Select>

                          <Select
                            value={values.employedSinceMonth}
                            onChange={handleChange("employedSinceMonth")}
                            onBlur={handleBlur("employedSinceMonth")}
                            bg="white"
                            h="40px"
                            fontSize="sm"
                            borderRadius="lg"
                            borderColor="gray.300"
                            _focus={{ borderColor: "black", boxShadow: "none" }}
                            _hover={{ borderColor: "gray.400" }}
                            maxW="90px"
                            required
                          >
                            <option value="" disabled>
                              mm
                            </option>
                            {monthOptions.map((month) => (
                              <option key={month} value={month}>
                                {month}
                              </option>
                            ))}
                          </Select>
                        </HStack>
                        <FormErrorMessage>{errors.employedSinceYear || errors.employedSinceMonth}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={Boolean(errors.employmentStatus)} position="relative">
                        <FormLabel
                          position="absolute"
                          left="12px"
                          top={isFloating("employmentStatus") ? "-9px" : "13px"}
                          zIndex={2}
                          px={2}
                          bg="white"
                          fontSize={isFloating("employmentStatus") ? "xs" : "sm"}
                          color={isFloating("employmentStatus") ? "gray.700" : "gray.500"}
                          pointerEvents="none"
                          transition="all 0.2s ease"
                          m={0}
                        >
                          Employment Status
                        </FormLabel>
                        <Select
                          value={values.employmentStatus}
                          onChange={handleChange("employmentStatus")}
                          onFocus={handleFocus("employmentStatus")}
                          onBlur={handleBlur("employmentStatus")}
                          bg="white"
                          h="52px"
                          borderRadius="lg"
                          borderColor="gray.300"
                          _focus={{ borderColor: "black", boxShadow: "none" }}
                          _hover={{ borderColor: "gray.400" }}
                          required
                        >
                          <option value="" disabled hidden />
                          {employmentStatusOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.employmentStatus}</FormErrorMessage>
                      </FormControl>
                    </>
                  )}

                  {isEmploymentIncome && (
                    <Box gridColumn={{ base: "span 1", md: "span 2" }} borderTop="1px solid" borderColor="gray.200" my={1} />
                  )}

                  <FormControl isRequired isInvalid={Boolean(errors.netMonthlyIncome)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("netMonthlyIncome") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("netMonthlyIncome") ? "xs" : "sm"}
                      color={isFloating("netMonthlyIncome") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Net Pay Per Cheque
                    </FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={values.netMonthlyIncome}
                      onChange={handleChange("netMonthlyIncome")}
                      onFocus={handleFocus("netMonthlyIncome")}
                      onBlur={handleBlur("netMonthlyIncome")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.netMonthlyIncome}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.payrollType)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("payrollType") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("payrollType") ? "xs" : "sm"}
                      color={isFloating("payrollType") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Payroll type
                    </FormLabel>
                    <Select
                      value={values.payrollType}
                      onChange={handleChange("payrollType")}
                      onFocus={handleFocus("payrollType")}
                      onBlur={handleBlur("payrollType")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    >
                      <option value="" disabled hidden />
                      {payrollTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.payrollType}</FormErrorMessage>
                  </FormControl>

                  <Box gridColumn={{ base: "span 1", md: "span 2" }}>
                    <SimpleGrid columns={{ base: 1, md: hasPaymentFrequency ? 2 : 1 }} spacing={4}>
                      <FormControl isRequired isInvalid={Boolean(errors.paymentFrequency)} position="relative">
                        <FormLabel
                          position="absolute"
                          left="12px"
                          top={isFloating("paymentFrequency") ? "-9px" : "13px"}
                          zIndex={2}
                          px={2}
                          bg="white"
                          fontSize={isFloating("paymentFrequency") ? "xs" : "sm"}
                          color={isFloating("paymentFrequency") ? "gray.700" : "gray.500"}
                          pointerEvents="none"
                          transition="all 0.2s ease"
                          m={0}
                        >
                          Payment frequency
                        </FormLabel>
                        <Select
                          value={values.paymentFrequency}
                          onChange={handleChange("paymentFrequency")}
                          onFocus={handleFocus("paymentFrequency")}
                          onBlur={handleBlur("paymentFrequency")}
                          bg="white"
                          h="52px"
                          borderRadius="lg"
                          borderColor="gray.300"
                          _focus={{ borderColor: "black", boxShadow: "none" }}
                          _hover={{ borderColor: "gray.400" }}
                          required
                        >
                          <option value="" disabled hidden />
                          {paymentFrequencyOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.paymentFrequency}</FormErrorMessage>
                      </FormControl>

                      {hasPaymentFrequency && (
                        <FormControl
                          isRequired
                          isInvalid={Boolean(errors.payday)}
                          position="relative"
                          gridColumn={{ base: isSemiMonthly ? "span 2" : "span 1", md: isSemiMonthly ? "span 2" : "span 1" }}
                        >
                          {!isSemiMonthly && (
                            <FormLabel
                              position="absolute"
                              left="12px"
                              top={isFloating("payday") ? "-9px" : "13px"}
                              zIndex={2}
                              px={2}
                              bg="white"
                              fontSize={isFloating("payday") ? "xs" : "sm"}
                              color={isFloating("payday") ? "gray.700" : "gray.500"}
                              pointerEvents="none"
                              transition="all 0.2s ease"
                              m={0}
                            >
                              Payday
                            </FormLabel>
                          )}

                          {isSemiMonthly && (
                            <FormLabel color="gray.700" fontSize="sm" m={0} mb={2}>
                              Payday
                            </FormLabel>
                          )}

                          {isMonthly && (
                            <Select
                              value={values.payday}
                              onChange={handleChange("payday")}
                              onFocus={handleFocus("payday")}
                              onBlur={handleBlur("payday")}
                              bg="white"
                              h="52px"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              required
                            >
                              <option value="" disabled hidden />
                              {dayOfMonthWithLastOptions.map((day) => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))}
                            </Select>
                          )}

                          {isWeeklyOrBiweekly && (
                            <Select
                              value={values.payday}
                              onChange={handleChange("payday")}
                              onFocus={handleFocus("payday")}
                              onBlur={handleBlur("payday")}
                              bg="white"
                              h="52px"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              required
                            >
                              <option value="" disabled hidden />
                              {weekdayOptions.map((day) => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))}
                            </Select>
                          )}

                          {isSemiMonthly && (
                            <VStack align="stretch" spacing={3}>
                              <HStack spacing={2} align="center" flexWrap="wrap">
                                <Select
                                  value={values.semiMonthlyFirstDay}
                                  onChange={handleChange("semiMonthlyFirstDay")}
                                  onBlur={handleBlur("payday")}
                                  bg="white"
                                  h="40px"
                                  fontSize="sm"
                                  borderRadius="lg"
                                  borderColor="gray.300"
                                  _focus={{ borderColor: "black", boxShadow: "none" }}
                                  _hover={{ borderColor: "gray.400" }}
                                  required
                                  maxW={{ base: "100%", md: "155px" }}
                                >
                                  <option value="" disabled hidden />
                                  {semiMonthlyDayOptions.map((day) => (
                                    <option key={`first-${day}`} value={day}>
                                      {day}
                                    </option>
                                  ))}
                                </Select>

                                <Text color="gray.600" fontSize="sm">
                                  and
                                </Text>

                                <Select
                                  value={values.semiMonthlySecondDay}
                                  onChange={handleChange("semiMonthlySecondDay")}
                                  onBlur={handleBlur("payday")}
                                  bg="white"
                                  h="40px"
                                  fontSize="sm"
                                  borderRadius="lg"
                                  borderColor="gray.300"
                                  _focus={{ borderColor: "black", boxShadow: "none" }}
                                  _hover={{ borderColor: "gray.400" }}
                                  required
                                  maxW={{ base: "100%", md: "155px" }}
                                >
                                  <option value="" disabled hidden />
                                  {semiMonthlyDayOptions.map((day) => (
                                    <option key={`second-${day}`} value={day}>
                                      {day}
                                    </option>
                                  ))}
                                </Select>

                                <Text color="gray.600" fontSize="sm">
                                  of month
                                </Text>
                              </HStack>

                              {isSemiMonthlyOtherSelected && (
                                <Input
                                  value={values.semiMonthlyOtherPaydays}
                                  onChange={handleChange("semiMonthlyOtherPaydays")}
                                  onBlur={handleBlur("payday")}
                                  bg="white"
                                  h="40px"
                                  fontSize="sm"
                                  borderRadius="lg"
                                  borderColor="gray.300"
                                  _focus={{ borderColor: "black", boxShadow: "none" }}
                                  _hover={{ borderColor: "gray.400" }}
                                  placeholder="Please enter paydays"
                                  required
                                />
                              )}
                            </VStack>
                          )}

                          {!isMonthly && !isWeeklyOrBiweekly && !isSemiMonthly && (
                            <Select isDisabled bg="gray.100" h="52px" borderRadius="lg" borderColor="gray.300">
                              <option></option>
                            </Select>
                          )}

                          <FormErrorMessage>{errors.payday}</FormErrorMessage>
                        </FormControl>
                      )}
                    </SimpleGrid>
                  </Box>

                  <FormControl isRequired isInvalid={Boolean(errors.lastPayday)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("lastPayday") ? "-9px" : "16px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("lastPayday") ? "xs" : "sm"}
                      color={isFloating("lastPayday") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Last Payday
                    </FormLabel>
                    <Input
                      type="date"
                      value={values.lastPayday}
                      onChange={handleChange("lastPayday")}
                      onFocus={handleFocus("lastPayday")}
                      onBlur={handleBlur("lastPayday")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    />
                    <FormErrorMessage>{errors.lastPayday}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.nextPayday)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("nextPayday") ? "-9px" : "16px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("nextPayday") ? "xs" : "sm"}
                      color={isFloating("nextPayday") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Next Payday
                    </FormLabel>
                    <Input
                      type="date"
                      value={values.nextPayday}
                      onChange={handleChange("nextPayday")}
                      onFocus={handleFocus("nextPayday")}
                      onBlur={handleBlur("nextPayday")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    />
                    <FormErrorMessage>{errors.nextPayday}</FormErrorMessage>
                  </FormControl>

                  {additionalIncomeSections.map((section, index) => {
                    const sectionIsWeeklyOrBiweekly = section.paymentFrequency === "Weekly" || section.paymentFrequency === "Bi-weekly";
                    const sectionIsMonthly = section.paymentFrequency === "Monthly";
                    const sectionIsSemiMonthly = section.paymentFrequency === "Semi-monthly";
                    const sectionPaydayInvalid = touched.additionalIncome && !getAdditionalPaydayValidity(section);

                    return (
                      <Box key={section.id} gridColumn={{ base: "span 1", md: "span 2" }} border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                        <HStack justify="space-between" align="center" mb={3}>
                          <Text color="gray.700" fontWeight="600">
                            Additional income {index + 1}
                          </Text>
                          <IconButton
                            aria-label="Close additional income section"
                            icon={<CloseIcon boxSize={2.5} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveIncomeSection(section.id)}
                          />
                        </HStack>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                          <FormControl isRequired isInvalid={touched.additionalIncome && !section.incomeType}>
                            <FormLabel m={0} mb={1} color="gray.700" fontSize="sm">
                              Income type
                            </FormLabel>
                            <Select
                              value={section.incomeType}
                              onChange={handleAdditionalIncomeChange(section.id, "incomeType")}
                              bg="white"
                              h="40px"
                              fontSize="sm"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              required
                            >
                              <option value="" disabled>
                                Select income type
                              </option>
                              {incomeTypeOptions.map((option) => (
                                <option key={`${section.id}-income-${option}`} value={option}>
                                  {option}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl isRequired isInvalid={touched.additionalIncome && !section.netMonthlyIncome}>
                            <FormLabel m={0} mb={1} color="gray.700" fontSize="sm">
                              Net monthly income
                            </FormLabel>
                            <Input
                              type="number"
                              min="0"
                              value={section.netMonthlyIncome}
                              onChange={handleAdditionalIncomeChange(section.id, "netMonthlyIncome")}
                              bg="white"
                              h="40px"
                              fontSize="sm"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              placeholder=""
                              required
                            />
                          </FormControl>

                          <FormControl isRequired isInvalid={touched.additionalIncome && !section.payrollType}>
                            <FormLabel m={0} mb={1} color="gray.700" fontSize="sm">
                              Payroll type
                            </FormLabel>
                            <Select
                              value={section.payrollType}
                              onChange={handleAdditionalIncomeChange(section.id, "payrollType")}
                              bg="white"
                              h="40px"
                              fontSize="sm"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              required
                            >
                              <option value="" disabled>
                                Select payroll type
                              </option>
                              {payrollTypeOptions.map((option) => (
                                <option key={`${section.id}-payroll-${option}`} value={option}>
                                  {option}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl isRequired isInvalid={touched.additionalIncome && !section.paymentFrequency}>
                            <FormLabel m={0} mb={1} color="gray.700" fontSize="sm">
                              Payment frequency
                            </FormLabel>
                            <Select
                              value={section.paymentFrequency}
                              onChange={handleAdditionalIncomeChange(section.id, "paymentFrequency")}
                              bg="white"
                              h="40px"
                              fontSize="sm"
                              borderRadius="lg"
                              borderColor="gray.300"
                              _focus={{ borderColor: "black", boxShadow: "none" }}
                              _hover={{ borderColor: "gray.400" }}
                              required
                            >
                              <option value="" disabled>
                                Select payment frequency
                              </option>
                              {paymentFrequencyOptions.map((option) => (
                                <option key={`${section.id}-frequency-${option}`} value={option}>
                                  {option}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl
                            isRequired
                            isInvalid={sectionPaydayInvalid}
                            gridColumn={{ base: "span 1", md: sectionIsSemiMonthly ? "span 2" : "span 1" }}
                          >
                            <FormLabel m={0} mb={1} color="gray.700" fontSize="sm">
                              Payday
                            </FormLabel>

                            {sectionIsMonthly && (
                              <Select
                                value={section.payday}
                                onChange={handleAdditionalIncomeChange(section.id, "payday")}
                                bg="white"
                                h="40px"
                                fontSize="sm"
                                borderRadius="lg"
                                borderColor="gray.300"
                                _focus={{ borderColor: "black", boxShadow: "none" }}
                                _hover={{ borderColor: "gray.400" }}
                                required
                              >
                                {dayOfMonthWithLastOptions.map((day) => (
                                  <option key={`${section.id}-monthly-${day}`} value={day}>
                                    {day}
                                  </option>
                                ))}
                              </Select>
                            )}

                            {sectionIsWeeklyOrBiweekly && (
                              <Select
                                value={section.payday}
                                onChange={handleAdditionalIncomeChange(section.id, "payday")}
                                bg="white"
                                h="40px"
                                fontSize="sm"
                                borderRadius="lg"
                                borderColor="gray.300"
                                _focus={{ borderColor: "black", boxShadow: "none" }}
                                _hover={{ borderColor: "gray.400" }}
                                required
                              >
                                {weekdayOptions.map((day) => (
                                  <option key={`${section.id}-weekly-${day}`} value={day}>
                                    {day}
                                  </option>
                                ))}
                              </Select>
                            )}

                            {sectionIsSemiMonthly && (
                              <VStack align="stretch" spacing={2}>
                                <HStack spacing={2} align="center" flexWrap="wrap">
                                  <Select
                                    value={section.semiMonthlyFirstDay}
                                    onChange={handleAdditionalIncomeChange(section.id, "semiMonthlyFirstDay")}
                                    bg="white"
                                    h="40px"
                                    fontSize="sm"
                                    borderRadius="lg"
                                    borderColor="gray.300"
                                    _focus={{ borderColor: "black", boxShadow: "none" }}
                                    _hover={{ borderColor: "gray.400" }}
                                    required
                                    maxW={{ base: "100%", md: "155px" }}
                                  >
                                    <option value="" disabled hidden />
                                    {semiMonthlyDayOptions.map((day) => (
                                      <option key={`${section.id}-first-day-${day}`} value={day}>
                                        {day}
                                      </option>
                                    ))}
                                  </Select>

                                  <Text color="gray.600" fontSize="sm">
                                    and
                                  </Text>

                                  <Select
                                    value={section.semiMonthlySecondDay}
                                    onChange={handleAdditionalIncomeChange(section.id, "semiMonthlySecondDay")}
                                    bg="white"
                                    h="40px"
                                    fontSize="sm"
                                    borderRadius="lg"
                                    borderColor="gray.300"
                                    _focus={{ borderColor: "black", boxShadow: "none" }}
                                    _hover={{ borderColor: "gray.400" }}
                                    required
                                    maxW={{ base: "100%", md: "155px" }}
                                  >
                                    <option value="" disabled hidden />
                                    {semiMonthlyDayOptions.map((day) => (
                                      <option key={`${section.id}-second-day-${day}`} value={day}>
                                        {day}
                                      </option>
                                    ))}
                                  </Select>

                                  <Text color="gray.600" fontSize="sm">
                                    of month
                                  </Text>
                                </HStack>

                                {(section.semiMonthlyFirstDay === "Other" || section.semiMonthlySecondDay === "Other") && (
                                  <Input
                                    value={section.semiMonthlyOtherPaydays || ""}
                                    onChange={handleAdditionalIncomeChange(section.id, "semiMonthlyOtherPaydays")}
                                    bg="white"
                                    h="40px"
                                    fontSize="sm"
                                    borderRadius="lg"
                                    borderColor="gray.300"
                                    _focus={{ borderColor: "black", boxShadow: "none" }}
                                    _hover={{ borderColor: "gray.400" }}
                                    placeholder="Please enter paydays"
                                    required
                                  />
                                )}
                              </VStack>
                            )}

                            {!sectionIsMonthly && !sectionIsWeeklyOrBiweekly && !sectionIsSemiMonthly && (
                              <Select isDisabled bg="gray.100" h="40px" fontSize="sm" borderRadius="lg" borderColor="gray.300">
                                <option>Select payment frequency first</option>
                              </Select>
                            )}

                            {sectionPaydayInvalid && <FormErrorMessage>Please complete payday for this income section</FormErrorMessage>}
                          </FormControl>
                        </SimpleGrid>
                      </Box>
                    );
                  })}

                  <FormControl isInvalid={Boolean(errors.additionalIncome)} gridColumn={{ base: "span 1", md: "span 2" }}>
                    <HStack spacing={2} align="center">
                      <FormLabel m={0} color="gray.700" fontSize="sm">
                        Add more income
                      </FormLabel>
                      <IconButton
                        aria-label="Add income section"
                        icon={<AddIcon boxSize={2.5} />}
                        type="button"
                        onClick={handleAddIncomeSection}
                        size="sm"
                        variant="outline"
                        color="gray.700"
                        bg="white"
                        borderColor="gray.300"
                        borderRadius="md"
                        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
                      />
                    </HStack>
                    <FormErrorMessage>{errors.additionalIncome}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <HStack spacing={3} justify="space-between">
                  <Button type="button" variant="outline" borderColor="gray.300" color="gray.700" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.50" }} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" isLoading={isSubmitting} bg="black" color="white" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.900" }}>
                    Continue
                  </Button>
                </HStack>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
